/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  ComponentContextCreateOptions,
  SingleValueParameter,
  ReportDefinition,
  MultiValueParameter,
  ParameterBase,
  MultiplicityType,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { MDDEResult } from "@sa/rep-mdde";
import { SelectionMode, AxisPosition, AggregationType, DataType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, MDDEQuery, OLAPTransformer, DataSourcesService, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { DictionariesService } from "@sa/rep-mdm";
import { last } from "lodash";
import { clone } from "../../../../shared";

export class ContextReport1 implements IComponentContext {
  public id: string;
  public loading: boolean;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  protected _exportParameters = new Map<string, ParameterBase>();

  protected cube = new BehaviorSubject<CubeDefinition | null>(null);
  public payloadChart1 = new BehaviorSubject<OLAPResult | null>(null);
  public payloadChart2 = new BehaviorSubject<OLAPResult | null>(null);

  public sources$ = new BehaviorSubject<string | null>(null);
  public netTradeCountries$ = new BehaviorSubject<number[] | null>(null);

  protected _calendar: BehaviorSubject<string[]>;
  public get calendar(): Observable<string[]> {
    return this._calendar.asObservable();
  } 

  protected _commodities: BehaviorSubject<string[]>;
  public get commodities(): Observable<string[]> {
    return this._commodities.asObservable();
  }

  protected _business: BehaviorSubject<number>;
  public get business(): Observable<number> {
    return this._business.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.loading = false;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;
    const calendarParameter = createOptions.dataflowService.getParameter("@calendar") as MultiValueParameter;
    this._calendar = new BehaviorSubject(calendarParameter.value as string[]);
    const commoditiesParameter = createOptions.dataflowService.getParameter("@commodities") as MultiValueParameter;
    this._commodities = new BehaviorSubject(commoditiesParameter.value as string[]);
    const businessParameter = createOptions.dataflowService.getParameter("@business-units") as SingleValueParameter;
    this._business = new BehaviorSubject(businessParameter.value as number);

    combineLatest([this._calendar, this._commodities, this._business])
      .pipe(debounceTime(500))
      .subscribe(([calendar, commodities, business]) => {
        this.query({ calendar, commodities, business });
      });

    const exportParameters = [
      "@e_calendar", "@e_crop_calendar", "@e_countries_net_trade", "@commodities", "@business-units"
    ];
    for (const eParamName of exportParameters) {
      let eParameter = this.options.dataflowService.getParameter(eParamName);
      if (eParameter === undefined) {
        this.options.dataflowService.parameters.add({
          id: eParamName,
          multiplicityType: MultiplicityType.Multiple,
          type: DataType.Number,
          allowNullable: false,
          value: []
        });
        eParameter = this.options.dataflowService.getParameter(eParamName);
      }
      this._exportParameters.set(eParamName, eParameter!);
    }
  }

  async tryIntegrate() {
    if (this.dataflowIntegration.canIntegrate && !this.dataflowIntegration.integrated) {
      this.dataflowIntegration.integrated = true;
      const cube = await dataService.getCubeDefinition("facts_slides");
      this.cube.next(cube);
    }
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar");
    if (calendar !== undefined) {
      this._calendar.next(calendar as string[]);
    } 
    const commodities = command.values.get("@commodities");
    if (commodities !== undefined) {
      this._commodities.next(commodities as string[]);
    }
    const business = command.values.get("@business-units");
    if (business !== undefined) {
      this._business.next(business as number);
    }

  }

  async query(parameters: { calendar: string[]; commodities: string[]; business: number }) {

    if (this.dataflowIntegration.canIntegrate && !this.dataflowIntegration.integrated) {
      this.dataflowIntegration.integrated = true;
    }

    let { calendar, commodities, business } = parameters;
    const { dataSourcesService } = this.options;
    const calendarId = Number(calendar[calendar.length - 1]);
    this.readyState = ComponentContextReadyState.Loading;
    this.payloadChart1.next(null);
    this.payloadChart2.next(null);
    const cubeFactsDef = await dataService.getCubeDefinition("facts_slides");
    const cubeFacts = await dataService.getCube({
      id: "facts_slides",
      parameters: cubeFactsDef.parameters,
    });

    const commodityId = Number(commodities[0]);
    const sources = await dataSourcesService.getDataSources(1, business, commodityId);
    this.sources$.next(sources);

    const dictionaryService = this.options.serviceProvider.resolve(ServicesRegister.Dictionary) as DictionariesService;
    const dictSlideCountries = await dictionaryService.getDictionary({
      id: 'slides-countries-display',
      parameters: {
        'slide': 1,
        'olap_commodity': commodityId,
        'business_unit': business
      }
    });
    const dictSlideCountriesItems = await dictSlideCountries.getAll();
    const itemsSlideCountriesRelationship = new Map(dictSlideCountriesItems.map(x => [Number(x.getValue("olap_country")), Number(x.id)]));
    const itemsSlideCountries = dictSlideCountriesItems.map(x => Number(x.getValue("olap_country")));
    let itemsPositiveCountries: number[] | undefined = undefined;
    let itemsNegativeCountries: number[] | undefined = undefined;
    let calendarOffset = 0; 
    // Если не смогли вытянуть данные, смотри не дальше чем на прошлый год
    while ((itemsPositiveCountries === undefined || !itemsPositiveCountries.length) && calendarOffset > -2) {
      const query: any = {
        'p_commodity': commodityId,
        'p_calendar': calendarId + (10000 * calendarOffset),
        'p_global_unit': business
      };
      const dictionaryId = 'dict_f_lead_net_trade_countries';
      const dictPositiveCountries = await dictionaryService.getDictionary({
        id: dictionaryId,
        parameters: { ...query, 'p_positive': true, }
      });
      const dictNegativeCountries = await dictionaryService.getDictionary({
        id: dictionaryId,
        parameters: { ...query, 'p_positive': false, }
      });
      itemsPositiveCountries = (await dictPositiveCountries.getAll()).map(x => Number(x.id));
      itemsNegativeCountries = (await dictNegativeCountries.getAll()).map(x => Number(x.id));
      calendarOffset--;
    }

    let itemsSlidePositiveCountries = itemsPositiveCountries?.filter(id => itemsSlideCountries.includes(id)) || [];
    let itemsSlideNegativeCountries = itemsNegativeCountries?.filter(id => itemsSlideCountries.includes(id)) || [];
    if (itemsSlidePositiveCountries.length === 0) {
      itemsSlidePositiveCountries = itemsSlideCountries;
    }
    if (itemsSlideNegativeCountries.length === 0) {
      itemsSlideNegativeCountries = Array.from(itemsSlidePositiveCountries).reverse();
    }
    const positiveCountres = itemsSlidePositiveCountries.slice(0, 5);
    const negativeCountres = (itemsSlideNegativeCountries.slice(0, 5).reverse());
    const countries = positiveCountres.concat(negativeCountres);
    this.netTradeCountries$.next(countries);
    const worldCountry = business === 2 ? 20000 : 100231; 

    const eCountriesParameter = this._exportParameters.get("@e_countries_net_trade")! as MultiValueParameter;
    eCountriesParameter.value = countries.map(x => itemsSlideCountriesRelationship.get(x));
    if (eCountriesParameter.value.length === 0) {
      eCountriesParameter.value = [worldCountry];
    }
    const eCalendarParameter = this._exportParameters.get("@e_calendar")! as MultiValueParameter;
    eCalendarParameter.value = [-4, -3, -2, -1, 0].map(x => calendarId + (x * 10000));
    const eCropCalendarParameter = this._exportParameters.get("@e_crop_calendar")! as MultiValueParameter;
    eCropCalendarParameter.value = [-1, 0].map(x => calendarId + (x * 10000));  

    const cubeSlideDef = await dataService.getCubeDefinition("slide1");
    const chart1Parameters = clone(cubeSlideDef.parameters);
    chart1Parameters["p_slide"]!["defaultValue"] = '1';
    chart1Parameters["p_commodities"]!["defaultValue"] = commodities.join(',');
    chart1Parameters["p_countries"]!["defaultValue"] = [worldCountry].join(',');
    chart1Parameters["p_attributes"]!["defaultValue"] = [1, 17, 22].join(',');
    chart1Parameters["p_calendar"]!["defaultValue"] = [-4, -3, -2, -1, 0].map(x => calendarId + (x * 10000)).join(',');
    chart1Parameters["p_units"]!["defaultValue"] = [1, 7, 8, 9].join(',');
    chart1Parameters["p_business"]!["defaultValue"] = business.toString();

    const cubeSlideChart1 = await dataService.getCube({
      id: "slide1",
      parameters: chart1Parameters,
    });
    const chart2Parameters = clone(cubeSlideDef.parameters);
    chart2Parameters["p_slide"]!["defaultValue"] = '1';
    chart2Parameters["p_commodities"]!["defaultValue"] = commodities.join(',');
    chart2Parameters["p_countries"]!["defaultValue"] = countries.join(',');
    chart2Parameters["p_attributes"]!["defaultValue"] = [27].join(',');
    chart2Parameters["p_calendar"]!["defaultValue"] = [-1, 0].map(x => calendarId + (x * 10000)).join(',');
    chart2Parameters["p_units"]!["defaultValue"] = [8].join(',');
    chart2Parameters["p_business"]!["defaultValue"] = business.toString();

    const cubeSlideChart2 = await dataService.getCube({
      id: "slide1",
      parameters: chart2Parameters,
    });

    window['useFacts'] = function () {
      sessionStorage.setItem('facts', 'true');
    };
    window['useFunc'] = function () {
      sessionStorage.removeItem('facts');
    }
    const cubeChart1 = sessionStorage.getItem('facts') ? cubeFacts : cubeSlideChart1;
    const cubeChart2 = sessionStorage.getItem('facts') ? cubeFacts : cubeSlideChart2;

    const olapResult1$ = cubeChart1.query({
      columns: {
        showEmpty: false,
        parts: [
          { path: "calendar.default", selection: [-4, -3, -2, -1, 0].map(x => calendarId + (x * 10000)), selectionMode: SelectionMode.Include },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "countries.default", selection: [worldCountry], selectionMode: SelectionMode.Include },
          { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          { path: "units.default", selection: [1, 8, 9, 7], selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "attributes.default", selection: [1, 17, 22], selectionMode: SelectionMode.Include },
          { path: "Measures", measures: [{ id: "value", aggregationType: AggregationType.Sum }, { id: "search", aggregationType: AggregationType.None }] },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeChart1.id,
      },
    } as any);

    // TODO: Получение параметров по умолчанию из this.model

    const olapResult2$ = cubeChart2.query({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "countries.default",
            selection: countries, //[1020, 1031, 1033, 1037, 2033, 2034, 2035, 2037, 2066, 2083, 2084, 2088, 2092, 2097, 1001, 2001, 2002, 2003, 2005, 2007, 1004, 1005, 2012, 2013, 1023, 2015, 2016, 2017, 1008, 1030, 2019, 1009, 2020, 2021, 2025, 2006, 2010, 2027, 2008, 2028, 2029, 2031, 1010, 2038, 2042, 2040, 2041, 2043, 2011, 2044, 2045, 1012, 2047, 2024, 2048, 1014, 1015, 2050, 2051, 2052, 2053, 2023, 2055, 1016, 2054, 2058, 2056, 1017, 1018, 2057, 2059, 1019, 2060, 2061, 2062, 2064, 2067, 2068, 1021, 1022, 2069, 2070, 2071, 2074, 2075, 2076, 2073, 2077, 1024, 2078, 2079, 2080, 2081, 1026, 2026, 1027, 1028, 2086, 2046, 2030, 2087, 2089, 2091, 2093, 2094, 2095, 2098, 2099, 2101, 2100, 1029, 2102, 2104, 2110, 2106, 2107, 2108, 2111, 1032, 2112, 2082, 2113, 2014, 1034, 1036, 2022, 2009, 2065, 2039, 2032, 2105, 3008, 4002, 4004, 4008, 100005, 100007, 100012, 100081, 100097, 100107, 100122, 100153, 100159, 100180, 100184, 100230, 100231, 544869, 544971, 3002, 4001, 3005, 3006, 3007, 3004, 4003, 3009, 3010, 3011, 3012, 3014, 3015, 3016, 2128, 3017, 3019, 3020, 3021, 3022, 3023, 3024, 3025, 3027, 3028, 3029, 3030, 3031, 4006, 3032, 3033, 3034, 2115, 3035, 2114, 2116, 2117, 4007, 3036, 2120, 3037, 2124, 2123, 2125, 2119, 550000, 2004, 3001, 1003, 3003, 1006, 2018, 3013, 1011, 1011, 1011, 1011, 1011, 1011, 1011, 3018, 3018, 3018, 3018, 3018, 3018, 3018, 2049, 2049, 2049, 2049, 2049, 2049, 2049, 1007, 1007, 1007, 1007, 1007, 1007, 1007, 2063, 2063, 2063, 2063, 2063, 2063, 2063, 2072, 2072, 2072, 2072, 2072, 2072, 2072, 3026, 3026, 3026, 3026, 3026, 3026, 3026, 1025, 1025, 1025, 1025, 1025, 1025, 1025, 2085, 2085, 2085, 2085, 2085, 2085, 2085, 2126, 2126, 2126, 2126, 2126, 2126, 2126, 2090, 2090, 2090, 2090, 2090, 2090, 2090, 2103, 2103, 2103, 2103, 2103, 2103, 2103, 2109, 2109, 2109, 2109, 2109, 2109, 2109, 2118, 2118, 2118, 2118, 2118, 2118, 2118, 2096, 2096, 2096, 2096, 2096, 2096, 2096],
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "attributes.default", selection: [27], selectionMode: SelectionMode.Include },
          { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          { path: "units.default", selection: [8], selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: [-1, 0].map(x => calendarId + (x * 10000)),
            selectionMode: SelectionMode.Include,
          },
          {
            path: "Measures",
            measures: [{ id: "value", aggregationType: AggregationType.Sum }, { id: "search", aggregationType: AggregationType.None }],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeChart2.id,
      },
    } as any);


    this.readyState = ComponentContextReadyState.Loaded;
    let [olapResult1, olapResult2] = await Promise.all([olapResult1$, olapResult2$]);

    olapResult1 = dataSourcesService.clearDataSources(olapResult1);
    olapResult2 = dataSourcesService.clearDataSources(olapResult2);
    this.payloadChart1.next(olapResult1);
    this.payloadChart2.next(olapResult2);
  }
}
